import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { InMemoryCacheService, UtilsService } from 'src/app/common';
import { AnchorFilterOptions, BacklinksOptions, FilterType, LinkIntersectFilterOptions, ReferringDomainsOptions } from '../models/site-search-filter-options';
import { AnchorsRequest, BacklinksDomainPagesRequest, BacklinksRequest, DomainIntersectionRequest, ReferringDomainRequest } from '../models/site-search-request';
import { SeoApiService } from '../seo-api.service';
@Injectable({
  providedIn: 'root'
})
export class SiteSearchService {

  private _target: string;
  private _path: string;
  private _locationCode: number;
  private _languangeCode: string;

  private _filterContext: BacklinksOptions;
  
  private _searchClicked: BehaviorSubject<boolean>;

  constructor(public utils: UtilsService, public seoApiService: SeoApiService, private cache: InMemoryCacheService) { 
    this.utils.logger.info('SiteSearchService.constructor()');
    this._searchClicked = new BehaviorSubject(false);
    this._filterContext = this.getBacklinksFilterContext();
  }

  public serializeBacklinksPayload(target: string, filterContext: BacklinksOptions, filters: string = `["dofollow", "=", ${filterContext.doFollow}]`): BacklinksRequest {

    if (!target) {
      throw new Error('Target is required');
    }

    const data: BacklinksRequest =  {
      "target": this.utils.stripHost(target),
      "mode": filterContext?.mode?.value?.trim() ?? '',
      "filtersString": filters,
      "limit": filterContext?.limit ?? this.cache?.seoLimits?.siteSearchServiceBacklinks,
      "internalListLimit": filterContext?.internalListLimit ?? this.cache?.seoLimits?.siteSearchServiceBacklinksInternal,
      "backlinksStatusType": filterContext?.backlinksStatusType ?? '',
      "includeSubdomains": filterContext?.includeSubdomains ?? false,
      "backlinksFiltersString": filterContext?.backlinksFiltersString ?? ''
    }

    return data;
  }

  public serializeAnchorPayload(target: string, filterContext: BacklinksOptions, filters: string = `["dofollow", "=", ${filterContext.doFollow}]`): AnchorsRequest {

    if (!target) {
      throw new Error('Target is required');
    }

    const data: AnchorsRequest =  {
      "target": target,
      "limit": this.cache?.seoLimits?.siteSearchServiceAnchors,
      "orderByString": `["backlinks,desc"]`,
      "filtersString": filters,
      "internalListLimit": this.cache?.seoLimits?.siteSearchServiceAnchorsInternal,
      "backlinksStatusType": "",
      "backlinksFiltersString": "['dofollow', '=', true]",
      "includeSubdomains": filterContext.includeSubdomains,
      "excludeInternalBacklinks": false
  }

    return data;
  }

  public serializeReferringDomainPayload(target: string, filterContext: BacklinksOptions, filters: string = `["dofollow", "=", ${filterContext.doFollow}]`): ReferringDomainRequest {

    if (!target) {
      throw new Error('Target is required');
    }

    const data: ReferringDomainRequest =  {
      "target": target,
      "limit": this.cache?.seoLimits?.siteSearchServiceReferringDomain,
      "orderByString": filterContext.orderByString ?? `["rank,desc"]`,
      "excludeInternalBacklinks": true,
      "backlinksFiltersString": filterContext?.backlinksFiltersString ?? ``,
      "filtersString": filters
   }

    return data;
  }

  public serializeBacklinksDomainIntersectionPayload(targets: any, excludeTargets: string[], filterContext: BacklinksOptions, filters: string = `["dofollow", "=", ${filterContext.doFollow}]`): DomainIntersectionRequest {

    if (!targets) {
      throw new Error('Target is required');
    }

    const data: DomainIntersectionRequest = {
        "targetList": targets,
        "includeSubdomains": false,
        "excludeTargets": excludeTargets,
        "limit": 5,
        "orderByString": `["1.backlinks,desc"]`,
        "excludeInternalBacklinks": true,
        "internalListLimit": 0,
        "backlinksStatusType": "",  
        "backlinksFiltersString": filters
    }
    
    return data;
  }

  public serializeBacklinksDomainPages(target: string, filterContext: BacklinksOptions, filters: string = `["dofollow", "=", ${filterContext.doFollow}]`): AnchorsRequest {

    if (!target) {
      throw new Error('Target is required');
    }

    const data: BacklinksDomainPagesRequest =  {
      "target": target,
      "limit": this.cache?.seoLimits?.siteSearchServiceDomainPages,
      "orderByString": filterContext.orderByString,
      "filtersString": filters,
      "internalListLimit": this.cache?.seoLimits?.siteSearchServiceDomainPagesInternal,
      "backlinksStatusType": "",
      "backlinksFiltersString": filterContext?.backlinksFiltersString ?? '',
      "includeSubdomains": filterContext.includeSubdomains,
      "excludeInternalBacklinks": false
  }

    return data;
  }

  public async onSearchClicked(): Promise<boolean> {

    this.utils.logger.info('SiteSearchService.onSearchClicked()');
    this._searchClicked.next(true);

    return true;
  }

  private getBacklinksFilterContext(): BacklinksOptions {
    const context = {
      mode: null,
      backlinkTypes: null,
      doFollow: false,
      includeSubdomains: false,
      excludeExternalBacklinks: false,
      urlTypes: null,
      backlinksFiltersString: null,
      internalListLimit: this.cache?.seoLimits?.siteSearchServiceBacklinksInternal,
      backlinksStatusType: 'all',
      orderByString: '',
      pageTypes: null
     }

     return context;
  }

  get searchClicked(): Observable<boolean> {
    return this._searchClicked.asObservable();
  }

  get target(): string {
    return this._target;
  }

  set target(value: string) {
    this._target = value;
  }

  get path(): string {
    return this._path;
  }

  set path(value: string) {
    this._path = value;
  }

  get locationCode(): number {
    return this._locationCode;
  }

  set locationCode(value: number) {
    this._locationCode = value;
  }

  get languageCode(): string {
    return this._languangeCode;
  }

  set languageCode(value: string) {
    this._languangeCode = value;
  }

  get filterContext(): BacklinksOptions {
    return this._filterContext;
  }

}
